/**
 * IndexedDB-backed storage adapter for Supabase Auth.
 *
 * iOS Safari aggressively purges localStorage for "Add to Home Screen" PWAs
 * after ~7 days of inactivity. IndexedDB is treated as persistent storage and
 * survives much longer, keeping users logged in.
 *
 * Falls back to localStorage if IndexedDB is unavailable.
 */
import { get, set, del } from 'idb-keyval'

const IDB_AVAILABLE = typeof indexedDB !== 'undefined'

export const idbStorage = {
  async getItem(key: string): Promise<string | null> {
    if (!IDB_AVAILABLE) return localStorage.getItem(key)
    const value = await get<string>(key)
    // Migrate: if IndexedDB is empty but localStorage has the value,
    // pull it over so existing sessions aren't lost on first load.
    if (value === undefined) {
      const fallback = localStorage.getItem(key)
      if (fallback !== null) {
        await set(key, fallback)
      }
      return fallback
    }
    return value
  },

  async setItem(key: string, value: string): Promise<void> {
    if (!IDB_AVAILABLE) {
      localStorage.setItem(key, value)
      return
    }
    await set(key, value)
    // Mirror to localStorage as a backup
    try { localStorage.setItem(key, value) } catch { /* quota exceeded is ok */ }
  },

  async removeItem(key: string): Promise<void> {
    if (!IDB_AVAILABLE) {
      localStorage.removeItem(key)
      return
    }
    await del(key)
    localStorage.removeItem(key)
  },
}
